import { titleCase } from "utils/common";

export function mergeLists(objA = {}, objB = {}){
	/*
		This function takes two objects, with array values, and returns a new object where
		the input arrays under matching keys are concatenated together.

		This is useful when we want to mix validators that look only at the `value` in a dynamic adType's
		{ value, isDynamic, dynVarName } object, and validators that look at the whole object.

		We can use the `makeDynamicConfig` function for the validators that only look at the `value`,
		and a second configuration object for everything else.

		e.g.
		```
		const WARNINGS = mergeLists(
			makeDynamicConfig({
				headline: [
					requiredNotNull,
					maxLength(150),
				],
				landingPage: [
					requiredNotNull,
					isURL,
				],
			}),
			{
				headline: [uniqueVariableName],
				landingPage: [uniqueVariableName],
			}
		);
		```

		This will generate a new object, that's equivalent to:
		```
		{
			headline: [
				makeDynamic(requiredNotNull),
				makeDynamic(maxLength(50)),
				uniqueVariableName,
			],
			landingPage: [
				makeDynamic(requiredNotNull),
				makeDynamic(isURL),
				uniqueVariableName,
			],
		}
		```
	*/
	return Object.entries(objB).reduce((prev, [key, value]) => {
		if(prev[key]){
			prev[key] = [
				...prev[key],
				...value
			];
		} else {
			prev[key] = [...value];
		}
		return prev;
	}, { ...objA });
}

export function uniqueVariableNameValidator(config = {}){
	/*
		This returns a validator function with the appropriate configuration for card and PAC fields
		The validation for all cases is very similar, so it's not worth duplicating the logic for
		three versions of the function, so we just curry in the configuration, and then we can
		generate three static validators to handle each type of field.
	*/
	const {
		isCard, // Used specifically for card fields (not the top level carousel creative fields)
		isPAC, // Used for any PAC fields, regardless of whether they're the default or other placement
	} = config || {};
	return (value, allValues, props, name) => {
		const {
			isDynamic: isCreativeDynamic,
			cards = [],
			placements = {},
			...restValues
		} = allValues || {};

		const dynVarName = value?.dynVarName;
		const isDynamic = value?.isDynamic;

		if(!isCreativeDynamic || !isDynamic || !dynVarName){
			/*
				If the field or Creative isn't dynamic, or if there's no dynVarName,
				we don't care if the dynVarName is unique
			*/
			return;
		}

		let otherValues = Object.entries(restValues);

		// PAC and card entries have to be filtered out before we alter their names for the otherValues entries.
		const cardEntries = cards.reduce((prev, card, cardIndex) => {
			const filteredCardEntries = Object.entries(card).filter(([cardFieldName]) => {
				// Cards are allowed to have matching variable names for equivalent fields in other cards
				return isCard ? (cardFieldName !== name) : true;
			});
			const mappedCardEntries = filteredCardEntries.map(([cardFieldName, cardValue]) => {
				/*
					Entries for card fields are modified to be prefixed with "Card <cardIndex+1>" so that
					the the user understands where the conflicting dynVarName is located when we generate the
					error message.
				*/
				return [`Card ${cardIndex+1} ${cardFieldName}`, cardValue];
			});

			return prev.concat(mappedCardEntries);
		}, []);
		otherValues.push(...cardEntries);

		const placementEntries = Object.entries(placements).reduce((prev, [placementName, placementValues]) => {
			const filteredPlacementEntries = Object.entries(placementValues).filter(([placementFieldName]) => {
				// Placements are allowed to have matching variable names for equivalent fields
				return isPAC ? (placementFieldName !== name) : true;
			});
			const mappedPlacementEntries = filteredPlacementEntries.map(([placementFieldName, placementValue]) => {
				/*
					Entries for placement fields are modified to be prefixed with the placement name so that
					the the user understands where the conflicting dynVarName is located when we generate the
					error message.
				*/
				return [`${placementName} ${placementFieldName}`, placementValue];
			});

			return prev.concat(mappedPlacementEntries);
		}, []);
		otherValues.push(...placementEntries);

		otherValues = otherValues.filter(([key, v]) => {
			return v !== value && v?.isDynamic && v?.dynVarName; /*
				We just filter out any field that isn't dynamic, doesn't have a dynVarName, or is the current field
			*/
		});

		const lowerCaseDynVarName = dynVarName.toLowerCase?.();
		const match = otherValues.find(([key, v]) => {
			if(isPAC){
				// Placement keys are prefixed by the placement name, which we're not interested in here.
				const tokens = key.split(" ");
				const baseName = tokens[tokens.length - 1];

				if(baseName === name){
					/*
						If a placement field's base name is the same as the name of the field we're
						comparing against, we're comparing the placement to the default placement,
						which is allowed to have the same dynVarName, so it's not a conflict match.
					*/
					return false;
				}
			}
			// variable names are case-insensitive, so we convert both sides to lower case
			return (v?.dynVarName?.toLowerCase?.() === lowerCaseDynVarName);
		});

		// If found, match[0] is the name of the conflicting field, which we titleCase to be readable
		return match && `${titleCase(match[0])} is already linked to a variable of that name`;
	};
}

export const uniqueVariableName = uniqueVariableNameValidator();

// Used for card validators -- Not for Creative-level carousel fields.
export const uniqueCardVariableName = uniqueVariableNameValidator({ isCard: true });

// Used for any PAC fields
export const uniquePACVariableName = uniqueVariableNameValidator({ isPAC: true });
